import {Component, OnDestroy} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
    CreationMethod,
    MsisdnApiService,
    MsisdnCreationMethod,
    MsisdnsResponse,
    ReferenceBookResponse,
    ReferenceBookType,
    SubjectApiService,
    SubjectCategoriesResponse,
    SubjectCategory,
    SubjectType,
} from '@npck/af-frontend-api';
import {DATE_TIME_SERVER_FORMAT, normalizePhone, RouterService, rxSpinner, TAB} from '@npck/libs/commons';
import dayjs from 'dayjs';
import {combineLatest, Subscription} from 'rxjs';
import {
    fieldExtractor,
    FilterDictionaryField,
    FilterEvent,
    FilterField,
    FilterSelectField,
    getFormattedDateOrNull,
    SearchPlaceholderContext,
} from '../../components/full-table/filter/filter.component';
import {mergeTableEvent, TableEvent} from '../../components/full-table/full-table.component';
import {Column, ColumnType, Row, StatusColumn} from '../../components/full-table/table/table.component';
import {StatusStyle} from '../../components/status/status.component';
import {AuthHolderService} from '../../domain/auth-holder.service';
import {CACHE_EXPIRATION_INTERVAL} from '../../domain/constants';
import {DictionaryHolderService, findValueInDictionary} from '../../domain/dictionary-holder.service';
import {SUBJECT_ID} from '../../domain/query-params.keys';
import {SubjectFilterHolderService} from '../../domain/subject-filter-holder.service';
import {Path} from '../../path';

@Component({
    selector: 'af-subjects',
    templateUrl: './subjects.component.html',
    styleUrls: ['./subjects.component.scss'],
    standalone: false,
})
export class SubjectsComponent implements OnDestroy {
    tabs: SubjectsTab[] = [];
    activeTab: SubjectsTab;
    selectedTabIndex: number;
    isNpckAdmin: boolean = false;
    dataSource: Row[] = [];

    filterFields: FilterField[] = [];

    private subjectsSubscription: Subscription;

    constructor(
        public subjectFilterHolderService: SubjectFilterHolderService,
        private routerService: RouterService,
        private activatedRoute: ActivatedRoute,
        private subjectApiService: SubjectApiService,
        private msisdnApiService: MsisdnApiService,
        private authHolderService: AuthHolderService,
        private dictionaryHolderService: DictionaryHolderService,
    ) {
        this.tabs = [
            'tabSubjectEventsAndAttempts',
            'tabLoans',
            'tabCompromisedPaymentInstruments',
            'tabOtherIllegalIncidentsSubjects',
            'tabSuspiciousNumbers',
        ];
        this.isNpckAdmin = this.authHolderService.hasAccess(['npck-admin']);
        this.activeTab = this.activatedRoute.snapshot.queryParamMap.get(TAB) as SubjectsTab || 'tabSubjectEventsAndAttempts';
        this.selectedTabIndex = this.tabs.findIndex(tab => tab == this.activeTab);
        this.handleNavigation(this.selectedTabIndex);
    }

    ngOnDestroy(): void {
        this.subjectsSubscription?.unsubscribe();
    }

    handleNavigation($event: number): void {
        this.activeTab = this.tabs[$event];
        this.routerService.navigate(
            Path.SUBJECTS,
            {
                queryParams: {[TAB]: this.activeTab},
                queryParamsHandling: 'merge',
            });
        if (this.activeTab === 'tabSuspiciousNumbers') {
            this.filterFields = [
                {
                    type: 'text',
                    name: 'query',
                },
                <FilterSelectField<string>>{
                    isMultiSelect: true,
                    type: 'select',
                    name: 'status',
                    values: ['deleted', 'active'],
                },
                {
                    type: 'dateRange',
                    name: 'dateRange',
                } as FilterField,
                {
                    type: 'dateRange',
                    name: 'deletedDateRange',
                } as FilterField,
                <FilterSelectField<MsisdnCreationMethod>>{
                    isMultiSelect: true,
                    type: 'select',
                    name: 'creationMethod',
                    values: Object.values(MsisdnCreationMethod),
                },
                <FilterDictionaryField>{
                    isMultiSelect: true,
                    type: 'select',
                    name: 'sourceOrganizations',
                    dictionaryName: 'bin',
                    values: [],
                },
            ];
            this.dataSource = [];
        } else if (this.activeTab !== 'tabLoans') {
            this.filterFields = [
                {
                    type: 'text',
                    name: 'query',
                },
                <FilterSelectField<SubjectType>>{
                    isMultiSelect: true,
                    type: 'select',
                    name: 'subjectType',
                    values: Object.values(SubjectType),
                },
                <FilterDictionaryField>{
                    isMultiSelect: true,
                    type: 'select',
                    name: 'category',
                    dictionaryName: 'category',
                    values: [],
                    filterBy: this.getCategoryFilterBy(),
                },
                <FilterSelectField<string>>{
                    isMultiSelect: true,
                    type: 'select',
                    name: 'status',
                    values: ['deleted', 'active'],
                },
                <FilterField>{
                    type: 'dateRange',
                    name: 'dateRange',
                },
                <FilterSelectField<CreationMethod>>{
                    isMultiSelect: true,
                    type: 'select',
                    name: 'creationMethod',
                    values: Object.values(CreationMethod),
                },
                <FilterDictionaryField>{
                    isMultiSelect: true,
                    type: 'select',
                    name: 'sourceOrganizations',
                    dictionaryName: 'bin',
                    values: [],
                },
            ];
            this.dataSource = [];
        } else {
            this.filterFields = [];
            this.dataSource = [];
        }
        this.loadSubjects(this.subjectFilterHolderService.getState(this.activeTab).tableEvent);
    }

    loadSubjects(tableEvent: TableEvent): void {
        if (this.activeTab === 'tabSuspiciousNumbers') {
            this.loadMsisdns(tableEvent);
            return;
        }

        this.subjectsSubscription?.unsubscribe();

        const mergedEvent = mergeTableEvent(this.subjectFilterHolderService.getState(this.activeTab).tableEvent, tableEvent);
        const activeTabState = this.subjectFilterHolderService.storeTableState(this.activeTab, mergedEvent);
        this.dataSource = [];

        if (!this.hasActiveFilters(activeTabState.tableEvent.filter)) {
            return;
        }

        const isCacheExpired = dayjs().diff(activeTabState.cachedTime, 'seconds') >= CACHE_EXPIRATION_INTERVAL;
        if (!activeTabState.response || activeTabState.tableEvent !== tableEvent || isCacheExpired) {
            const filterEvent = activeTabState.tableEvent.filter;
            const sortEvent = activeTabState.tableEvent.sortBy;
            const pageEvent = activeTabState.tableEvent.page;
            const selectedDeletedStatuses = fieldExtractor(filterEvent?.status);
            let isDeleted = null;
            if (selectedDeletedStatuses?.length === 2) {
                isDeleted = null;
            } else if (selectedDeletedStatuses?.length) {
                isDeleted = selectedDeletedStatuses[0] === 'deleted';
            }
            const selectedCategories = fieldExtractor(filterEvent?.category);
            const defaultCategories = this.getCategoryFilterBy();
            this.subjectsSubscription = combineLatest([
                this.dictionaryHolderService.getReference(ReferenceBookType.BIN),
                this.subjectApiService.getSubjectCategories({
                    limit: pageEvent.pageSize,
                    offset: pageEvent.pageOffset,
                    query: filterEvent.query,
                    isDeleted: isDeleted,
                    sourceOrganizations: fieldExtractor(filterEvent?.sourceOrganizations),
                    category: selectedCategories?.length ? selectedCategories : defaultCategories,
                    subjectTypes: fieldExtractor(filterEvent?.subjectType) as SubjectType[],
                    creationMethods: fieldExtractor(filterEvent?.creationMethod) as CreationMethod[],
                    dateFrom: getFormattedDateOrNull(filterEvent?.dateRange?.dateFrom, DATE_TIME_SERVER_FORMAT),
                    dateTo: getFormattedDateOrNull(filterEvent?.dateRange?.dateTo, DATE_TIME_SERVER_FORMAT),
                    hasDeletedCategory: false,
                    shouldHideVictim: false,
                    sortBy: sortEvent?.columnName,
                    sortOrder: sortEvent?.direction,
                }),
            ])
                .pipe(rxSpinner(tableEvent.spinnerId))
                .subscribe(([organizations, response]) => {
                    this.subjectFilterHolderService.storeResponse(this.activeTab, response);
                    this.buildDataSource(response, organizations);
                });
        } else {
            this.subjectsSubscription = this.dictionaryHolderService.getReference(ReferenceBookType.BIN).subscribe(organizations =>
                this.buildDataSource(activeTabState.response as SubjectCategoriesResponse, organizations),
            );
        }
    }

    navigateToCreateSubject(): void {
        if (this.activeTab === 'tabSuspiciousNumbers') {
            return;
        }
        this.routerService.navigateToTab<SubjectsTab>(Path.SUBJECTS_CREATE, this.activeTab);
    }

    hasExportImport = (tab: SubjectsTab): boolean =>
        [
            'tabCompromisedPaymentInstruments',
            'tabOtherIllegalIncidentsSubjects',
            'tabSuspiciousNumbers',
        ].includes(tab);

    private getCategoryFilterBy(): string[] {
        switch (this.activeTab) {
            case 'tabSubjectEventsAndAttempts':
                return [SubjectCategory.GREY, SubjectCategory.BLACK, SubjectCategory.POTENTIAL_SUSPECT];
            case 'tabCompromisedPaymentInstruments':
                return [SubjectCategory.DRUG_CEPI_GREY, SubjectCategory.PYRAMID_CEPI_GREY, SubjectCategory.CASINO_CEPI_GREY];
            case 'tabOtherIllegalIncidentsSubjects':
                return [SubjectCategory.PYRAMID_GREY, SubjectCategory.CASINO_GREY, SubjectCategory.DRUG_GREY];
            default:
                return [];
        }
    }

    private hasActiveFilters(filter: FilterEvent): boolean {
        if (!filter) {
            return false;
        }
        if (filter.query?.trim()) {
            return true;
        }
        const selectFields = ['status', 'subjectType', 'category', 'creationMethod', 'sourceOrganizations'] as const;
        if (selectFields.some(f => fieldExtractor(filter[f])?.length)) {
            return true;
        }

        return !!((filter.dateRange && (filter.dateRange.dateFrom || filter.dateRange.dateTo))
            || (filter.deletedDateRange && (filter.deletedDateRange.dateFrom || filter.deletedDateRange.dateTo)));
    }

    private loadMsisdns(tableEvent: TableEvent): void {
        this.subjectsSubscription?.unsubscribe();

        const mergedEvent = mergeTableEvent(this.subjectFilterHolderService.getState(this.activeTab).tableEvent, tableEvent);
        const activeTabState = this.subjectFilterHolderService.storeTableState(this.activeTab, mergedEvent);
        this.dataSource = [];

        if (!this.hasActiveFilters(activeTabState.tableEvent.filter)) {
            return;
        }

        const isCacheExpired = dayjs().diff(activeTabState.cachedTime, 'seconds') >= CACHE_EXPIRATION_INTERVAL;
        if (!activeTabState.response || activeTabState.tableEvent !== tableEvent || isCacheExpired) {
            const filterEvent = activeTabState.tableEvent.filter;
            const sortEvent = activeTabState.tableEvent.sortBy;
            const pageEvent = activeTabState.tableEvent.page;
            this.subjectsSubscription = combineLatest([
                this.dictionaryHolderService.getReference(ReferenceBookType.BIN),
                this.msisdnApiService.getMsisdns({
                    limit: pageEvent.pageSize,
                    offset: pageEvent.pageOffset,
                    searchQuery: filterEvent?.query,
                    isDeleted: this.extractIsDeleted(filterEvent),
                    createdByOrganizations: fieldExtractor(filterEvent?.sourceOrganizations),
                    creationMethods: fieldExtractor(filterEvent?.creationMethod) as MsisdnCreationMethod[],
                    createdDateFrom: getFormattedDateOrNull(filterEvent?.dateRange?.dateFrom, DATE_TIME_SERVER_FORMAT),
                    createdDateTo: getFormattedDateOrNull(filterEvent?.dateRange?.dateTo, DATE_TIME_SERVER_FORMAT),
                    deletedDateFrom: getFormattedDateOrNull(filterEvent?.deletedDateRange?.dateFrom, DATE_TIME_SERVER_FORMAT),
                    deletedDateTo: getFormattedDateOrNull(filterEvent?.deletedDateRange?.dateTo, DATE_TIME_SERVER_FORMAT),
                    sortBy: sortEvent?.columnName,
                    sortOrder: sortEvent?.direction,
                }),
            ])
                .pipe(rxSpinner(tableEvent.spinnerId))
                .subscribe(([organizations, response]) => {
                    this.buildMsisdnDataSource(response, organizations);
                    this.subjectFilterHolderService.storeResponse(this.activeTab, response);
                });
        } else {
            this.subjectsSubscription = this.dictionaryHolderService.getReference(ReferenceBookType.BIN).subscribe(organizations =>
                this.buildMsisdnDataSource(activeTabState.response as MsisdnsResponse, organizations),
            );
        }
    }

    private extractIsDeleted(filterEvent: FilterEvent): boolean | null {
        const selectedStatuses = fieldExtractor(filterEvent?.status);
        if (!selectedStatuses?.length || selectedStatuses.length === 2) {
            return null;
        }
        return selectedStatuses[0] === 'deleted';
    }

    private buildMsisdnDataSource(response: MsisdnsResponse, organizations?: ReferenceBookResponse[]): void {
        this.dataSource = response.items.map(item => {
            const organizationDisplay = organizations
                ? (findValueInDictionary(organizations, item.createdByOrganization, 'name_ru') || item.createdByOrganization)
                : item.createdByOrganization;
            return <Row>{
                click: () => this.openMsisdnDetails(item.id),
                isDeleted: <StatusColumn>{
                    type: ColumnType.STATUS,
                    sortable: true,
                    text: item.isDeleted ? 'i18n_af_statusDeleted' : 'i18n_af_statusActive',
                    style: item.isDeleted ? StatusStyle.ERROR : StatusStyle.SUCCESS,
                },
                msisdn: <Column>{
                    type: ColumnType.TEXT,
                    text: normalizePhone(item.msisdn),
                },
                createdDate: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.createdDate,
                },
                createdBy: <Column>{
                    type: ColumnType.TEXT,
                    sortable: true,
                    text: item.createdBy,
                },
                deletedDate: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.deletedDate,
                },
                deletedBy: <Column>{
                    type: ColumnType.TEXT,
                    sortable: true,
                    text: item.deletedBy,
                },
                creationMethod: <Column>{
                    type: ColumnType.TEXT,
                    text: `i18n_af_filter_field_creationMethod#${item.creationMethod}`,
                },
                createdByOrganization: <Column>{
                    type: ColumnType.TEXT,
                    sortable: true,
                    text: organizationDisplay,
                },
                msisdnId: <Column>{
                    type: ColumnType.TEXT,
                    text: item.id,
                },
            };
        });
    }

    private buildDataSource(response: SubjectCategoriesResponse, organizations?: ReferenceBookResponse[]): void {
        this.dataSource = response.items.map(item => {
            const organizationDisplay = organizations
                ? (findValueInDictionary(organizations, item.createdByOrganization, 'name_ru') || item.createdByOrganization)
                : item.createdByOrganization;
            const personsColumns = <Row>{
                patronymic: <Column>{
                    type: ColumnType.TEXT,
                    text: item.patronymic,
                },
                surname: <Column>{
                    type: ColumnType.TEXT,
                    text: item.surname,
                },
            };

            let subjectColumns: Row;
            const activeFilter = this.subjectFilterHolderService.getState(this.activeTab)?.tableEvent?.filter;
            const selectedCategories = fieldExtractor(activeFilter?.subjectType);
            if (selectedCategories?.length > 1 || !selectedCategories?.length) {
                subjectColumns = <Row>{
                    ...personsColumns,
                };
            } else if (selectedCategories[0] === SubjectType.PERSON) {
                subjectColumns = <Row>{
                    ...personsColumns,
                };
            }

            return <Row>{
                click: () => this.openFraudDetails(item.subjectId, item.category),
                is_deleted_category: <StatusColumn>{
                    type: ColumnType.STATUS,
                    sortable: true,
                    text: item.isDeleted ? 'i18n_af_statusDeleted' : 'i18n_af_statusActive',
                    style: item.isDeleted ? StatusStyle.ERROR : StatusStyle.SUCCESS,
                },
                categories: <Column>{
                    type: ColumnType.TEXT,
                    text: `i18n_af_category#${item.category}`,
                },
                subjectType: <Column>{
                    type: ColumnType.TEXT,
                    text: `i18n_af_subjectType#${item.subjectType}`,
                },
                identification_number: <Column>{
                    type: ColumnType.TEXT,
                    text: item.identificationNumber,
                },
                name: <Column>{
                    type: ColumnType.TEXT,
                    text: item.name,
                },
                ...subjectColumns,
                created_date: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.createdDate,
                },
                created_by: <Column>{
                    type: ColumnType.TEXT,
                    text: item.createdBy,
                },
                updated_date: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.updatedDate,
                },
                updated_by: <Column>{
                    type: ColumnType.TEXT,
                    text: item.updatedBy,
                },
                deleted_date: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.deletedDate,
                },
                deleted_by: <Column>{
                    type: ColumnType.TEXT,
                    text: item.deletedBy,
                },
                creation_method: <Column>{
                    type: ColumnType.TEXT,
                    text: `i18n_af_filter_field_creationMethod#${item.creationMethod}`,
                },
                source_organization: <Column>{
                    type: ColumnType.TEXT,
                    text: organizationDisplay,
                },
                id: <Column>{
                    type: ColumnType.TEXT,
                    text: item.subjectId,
                },
            };
        });
    }

    private openFraudDetails(id: string, category?: SubjectCategory): void {
        this.routerService.navigateToTab<SubjectsTab>(
            Path.SUBJECT_DETAILS,
            this.activeTab,
            {
                pathParams: {[SUBJECT_ID]: id},
                ...(category ? {queryParams: {category}} : {}),
            },
        );
    }

    private openMsisdnDetails(msisdnId: string): void {
        this.routerService.navigateToTab<SubjectsTab>(`${Path.SUBJECTS}/msisdn/${msisdnId}`, 'tabSuspiciousNumbers');
    }
}

export declare type SubjectsTab =
    'tabSubjectEventsAndAttempts' |
    'tabLoans' |
    'tabCompromisedPaymentInstruments' |
    'tabOtherIllegalIncidentsSubjects' |
    'tabSuspiciousNumbers';

export interface SubjectTabInfo {
    tableEvent: TableEvent;
    placeholderContext: SearchPlaceholderContext;
    response?: SubjectCategoriesResponse | MsisdnsResponse;
    cachedTime: dayjs.Dayjs;
}
